import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import { useState } from "react";
import { IButtonBlockData } from "../../../types/content-block-data";
import { Link, Paintbrush, SignpostBig, PanelLeftDashed, Type, Unlink, SquareDashedTopSolid, RulerDimensionLine } from "lucide-react";
import bodyStylesManager from "../../../stores/body-styles-manager";
import { cleanDimenValues } from "../../../lib/page-builder-lib";
import SatoriTextBox from "../shared/satori-ui/textbox";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import { STANDARD_FONT_FAMILIES, BORDER_STYLES, ACTION_TYPES, ALIGN_OPTIONS, FONT_WEIGHTS, TEXT_DECORATIONS } from "../../../types/constants";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import SatoriCheckBox from "../shared/satori-ui/checkbox";

/**
 * ButtonOptions – Sidebar Editing Panel for Button Blocks
 *
 * Provides an extensive set of sidebar controls for editing all visual and functional properties
 * of a Button content block in the Satori PageBuilder. Allows configuration of label, link/action type,
 * URL, target behavior, colors, typography, spacing, borders, border radius, and layout options.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Button content block to edit.
 *
 * @example
 * // Render options panel for a given Button block
 * <ButtonOptions blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Button block's data from the MobX contentManager store.
 * - Renders grouped UI controls for all editable Button fields (text, action, appearance, spacing, layout, etc).
 * - All field updates call `contentManager.setButtonConfig`, ensuring state is updated atomically and reactively.
 * - Handles both linked and individual side controls for padding, margin, and border radius.
 * - Dimension controls (font size, spacing, etc) support px, rem, and em units with auto-conversion.
 * - This component is wrapped in MobX's `observer()` to enable real-time reactivity.
 *
 * @see contentManager.setButtonConfig
 * @see ButtonBlock.tsx – For how these properties affect rendering
 * @see ColumnsOptionsPanel – For a similar options pattern
 */
export default observer(function ButtonOptions({ blockId }: { blockId: string }) {
  // Look up the current block
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "button") return null;

  // Short alias for blockData
  const buttonData = block.blockData as IButtonBlockData || {};
  const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
  let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)

  if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
    parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
  else
    parentRawFontSize = parseInt(parentFontSize!.value);

  // Main UI
  return (
    <section className="flex flex-col">
      {/* 1. Button Action */}
      <SatoriCollapsiblePanel
        icon={<SignpostBig size={18} />}
        title={"Navigation"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          <SatoriDropDownList
            label={"Action Types"}
            options={ACTION_TYPES}
            value={buttonData.actionType ?? "url"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                actionType: val,
              })
            }
            isFontFamily={false}
          />
          {/* Conditional UI based on action type */}
          {buttonData.actionType === "email" ? (
            <SatoriTextBox
              type={"email"}
              value={buttonData.url ?? ""}
              onChange={(val) =>
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  url: val,
                })
              }
              placeholder="user@email.com"
            />
          ) : buttonData.actionType === "phone" ? (
            <SatoriTextBox
              type={"tel"}
              value={buttonData.url ?? ""}
              onChange={(val) =>
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  url: val,
                })
              }
              placeholder="tel:+1234567890"
            />
          ) : (
            <SatoriTextBox
              type={"url"}
              value={buttonData.url ?? ""}
              onChange={(val) =>
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  url: val,
                })
              }
              placeholder="https://www.example.com"
            />
          )}
          {/* Open in new tab (target) */}
          <label className="flex items-center gap-2 text-xs mt-1">
            <SatoriCheckBox
              checked={!!buttonData.target}
              onChange={(val) =>
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  target: val,
                })
              }
              label={"Open in new tab"}
            />
          </label>
        </div>
      </SatoriCollapsiblePanel>

      {/* 2. Colors & States */}
      <SatoriCollapsiblePanel
        icon={<Paintbrush size={18} />}
        title={"Appearance"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3 overflow-y-visible">
          {/* Text Color */}
          <SatoriColorPicker
            label={"Text"}
            value={buttonData.color || "#222222"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                color: val,
              })
            }
          />
          {/* Background Color */}
          <SatoriColorPicker
            label={"Background"}
            value={buttonData.backgroundColor ?? "#ffffff"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                backgroundColor: val,
              })
            }
          />
          {/* Hover Text Color */}
          <SatoriColorPicker
            label={"Hover Text"}
            value={buttonData.hoverColor || "#ffffff"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                hoverColor: val,
              })
            }
          />
          {/* Hover Background Color */}
          <SatoriColorPicker
            label={"Hover Background"}
            value={buttonData.hoverBackgroundColor || "#1d4ed8"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                hoverBackgroundColor: val,
              })
            }
          />
        </div>
      </SatoriCollapsiblePanel>

      {/* 3. Typography */}
      <SatoriCollapsiblePanel
        icon={<Type size={18} />}
        title={"Typography"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Button Label */}
          <SatoriTextBox
            label={"Button Label"}
            value={buttonData.label}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                label: val,
              })
            }
            placeholder="Button label"
          />
          {/* Font Family */}
          <SatoriDropDownList
            label={"Font Family"}
            value={buttonData.fontFamily || "Inter, sans-serif"}
            isFontFamily={true}
            onChange={(val) => {
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                fontFamily: val,
              });
            }}
          />
          {/* Font Size */}
          <SatoriDimensionsBox
            label={"Font Size"}
            value={cleanDimenValues(buttonData?.fontSize?.value!) || "1"}
            unit={buttonData?.fontSize?.unit || "rem"}
            onValueChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                fontSize: {
                  value: val,
                  unit: buttonData?.fontSize?.unit || "rem",
                },
              })
            }
            onUnitChange={(unitVal) => {
              if (unitVal === "px") {
                // We need to change from rem/em value to pixel value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  fontSize: {
                    value:
                      Math.round(
                        parseFloat(buttonData?.fontSize?.value!) *
                          parentRawFontSize
                      ).toString() || "",
                    unit: unitVal,
                  },
                });
              } else if (unitVal === "rem" || unitVal === "em") {
                // We need to change from pixel value to rem/em value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  fontSize: {
                    value:
                      (
                        parseFloat(buttonData?.fontSize?.value!) /
                        parentRawFontSize
                      )
                        .toFixed(3)
                        .toString() || "",
                    unit: unitVal,
                  },
                });
              }
            }}
          />
          {/* Font Weight */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Font Weight"}
            options={FONT_WEIGHTS}
            isFontFamily={false}
            value={buttonData.fontWeight || "500"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                fontWeight: val,
              })
            }
          />
          {/* Line Height */}
          <SatoriDimensionsBox
            label={"Line Height"}
            value={cleanDimenValues(buttonData.lineHeight?.value!) || "1.5"}
            unit={buttonData.lineHeight?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                lineHeight: {
                  value: val,
                  unit: buttonData?.lineHeight?.unit || "rem",
                },
              })
            }
            onUnitChange={(unitVal) => {
              if (unitVal === "px") {
                // We need to change from rem/em value to pixel value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  lineHeight: {
                    value:
                      Math.round(
                        parseFloat(buttonData?.lineHeight?.value!) *
                          parentRawFontSize
                      ).toString() || "",
                    unit: unitVal,
                  },
                });
              } else if (unitVal === "rem" || unitVal === "em") {
                // We need to change from pixel value to rem/em value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  lineHeight: {
                    value:
                      (
                        parseFloat(buttonData?.lineHeight?.value!) /
                        parentRawFontSize
                      )
                        .toFixed(3)
                        .toString() || "",
                    unit: unitVal,
                  },
                });
              }
            }}
          />
          {/* Letter Spacing */}
          <SatoriDimensionsBox
            label={"Letter Spacing"}
            value={cleanDimenValues(buttonData.letterSpacing?.value!) || "0"}
            unit={buttonData.letterSpacing?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                letterSpacing: {
                  value: val,
                  unit: buttonData?.letterSpacing?.unit || "rem",
                },
              })
            }
            onUnitChange={(unitVal) => {
              if (unitVal === "px") {
                // We need to change from rem/em value to pixel value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  letterSpacing: {
                    value:
                      Math.round(
                        parseFloat(buttonData?.letterSpacing?.value!) *
                          parentRawFontSize
                      ).toString() || "",
                    unit: unitVal,
                  },
                });
              } else if (unitVal === "rem" || unitVal === "em") {
                // We need to change from pixel value to rem/em value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  letterSpacing: {
                    value:
                      (
                        parseFloat(buttonData?.letterSpacing?.value!) /
                        parentRawFontSize
                      )
                        .toFixed(3)
                        .toString() || "",
                    unit: unitVal,
                  },
                });
              }
            }}
          />
          {/* Text Decoration */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Text Decoration"}
            options={TEXT_DECORATIONS}
            isFontFamily={false}
            value={buttonData.textDecoration || "none"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                textDecoration: val,
              })
            }
          />
        </div>
      </SatoriCollapsiblePanel>

      {/* 4. Spacing */}
      <SatoriCollapsiblePanel
        icon={<PanelLeftDashed size={18} />}
        title={"Spacing"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Button Padding sub-secton */}
          <div className="flex items-center gap-2 mb-2 w-full justify-between">
            <span className="text-base">Padding</span>
            <button
              onClick={() =>
                contentManager.setButtonConfig(blockId, {
                  padding: {
                    ...buttonData?.padding!,
                    linked: !buttonData?.padding?.linked,
                  },
                })
              }
              className="ml-1 p-1"
              title={
                buttonData?.padding?.linked ? "Unlink sides" : "Link all sides"
              }
            >
              {buttonData?.padding?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {buttonData?.padding?.linked ? (
            <div className="flex flex-col gap-2 mb-2">
              <SatoriDimensionsBox
                value={cleanDimenValues(buttonData?.padding?.left!) || ""}
                unit={buttonData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    padding: {
                      ...buttonData?.padding!,
                      top: val,
                      right: val,
                      bottom: val,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedValue = Math.round(
                      parseFloat(buttonData?.padding?.top!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedValue,
                        right: convertedValue,
                        bottom: convertedValue,
                        left: convertedValue,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedValue = (
                      parseFloat(buttonData?.padding?.top!) / parentRawFontSize
                    )
                      .toFixed(3)
                      .toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedValue,
                        right: convertedValue,
                        bottom: convertedValue,
                        left: convertedValue,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 mb-2 w-full">
              <SatoriDimensionsBox
                label={"Top"}
                value={cleanDimenValues(buttonData?.padding?.top!) || ""}
                unit={buttonData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    padding: {
                      ...buttonData?.padding!,
                      top: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Right"}
                value={cleanDimenValues(buttonData?.padding?.right!) || ""}
                unit={buttonData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    padding: {
                      ...buttonData?.padding!,
                      right: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom"}
                value={cleanDimenValues(buttonData?.padding?.bottom!) || ""}
                unit={buttonData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    padding: {
                      ...buttonData?.padding!,
                      bottom: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Left"}
                value={cleanDimenValues(buttonData?.padding?.left!) || ""}
                unit={buttonData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    padding: {
                      ...buttonData?.padding!,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.padding?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.padding?.right!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.padding?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.padding?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      padding: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.padding?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          )}

          {/* Button Margins sub-secton */}
          <div className="flex items-center gap-2 my-2 w-full justify-between">
            <span className="text-base">Button Margins</span>
            <button
              onClick={() =>
                contentManager.setButtonConfig(blockId, {
                  margin: {
                    ...buttonData?.margin!,
                    linked: !buttonData?.margin?.linked,
                  },
                })
              }
              className="ml-1 p-1"
              title={
                buttonData?.margin?.linked ? "Unlink sides" : "Link all sides"
              }
            >
              {buttonData?.margin?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {buttonData?.margin?.linked ? (
            <div className="flex flex-col gap-2">
              <SatoriDimensionsBox
                value={cleanDimenValues(buttonData?.margin?.left!) || ""}
                unit={buttonData?.margin?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    margin: {
                      ...buttonData?.margin!,
                      top: val,
                      right: val,
                      bottom: val,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedValue = Math.round(
                      parseFloat(buttonData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedValue,
                        right: convertedValue,
                        bottom: convertedValue,
                        left: convertedValue,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedValue = (
                      parseFloat(buttonData?.margin?.top!) / parentRawFontSize
                    )
                      .toFixed(3)
                      .toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedValue,
                        right: convertedValue,
                        bottom: convertedValue,
                        left: convertedValue,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <SatoriDimensionsBox
                label={"Top"}
                value={cleanDimenValues(buttonData?.margin?.top!) || ""}
                unit={buttonData?.margin?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    margin: {
                      ...buttonData?.margin!,
                      top: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Right"}
                value={cleanDimenValues(buttonData?.margin?.right!) || ""}
                unit={buttonData?.margin?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    margin: {
                      ...buttonData?.margin!,
                      right: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom"}
                value={cleanDimenValues(buttonData?.margin?.bottom!) || ""}
                unit={buttonData?.margin?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    margin: {
                      ...buttonData?.margin!,
                      bottom: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Left"}
                value={cleanDimenValues(buttonData?.margin?.left!) || ""}
                unit={buttonData?.margin?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    margin: {
                      ...buttonData?.margin!,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) *
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(buttonData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(buttonData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(buttonData?.margin?.bottom!) /
                        parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(buttonData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: buttonData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          )}
        </div>
      </SatoriCollapsiblePanel>

      <SatoriCollapsiblePanel
        icon={<SquareDashedTopSolid size={18} />}
        title={"Border Styles"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Border Style */}
          <SatoriDropDownList
            layout="horizontal"
            isFontFamily={false}
            label={"Border Style"}
            options={BORDER_STYLES}
            value={buttonData.borderStyle || "solid"}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                borderStyle: val,
              })
            }
          />
          {/* Border Width */}
          <SatoriDimensionsBox
            label={"Border Width"}
            value={cleanDimenValues(buttonData.borderWidth?.value!) || "0"}
            unit={buttonData.borderWidth?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                borderWidth: {
                  value: val,
                  unit: buttonData?.borderWidth?.unit || "rem",
                },
              })
            }
            onUnitChange={(unitVal) => {
              if (unitVal === "px") {
                // We need to change from rem/em value to pixel value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  borderWidth: {
                    value:
                      Math.round(
                        parseFloat(buttonData?.borderWidth?.value!) *
                          parentRawFontSize
                      ).toString() || "",
                    unit: unitVal,
                  },
                });
              } else if (unitVal === "rem" || unitVal === "em") {
                // We need to change from pixel value to rem/em value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  borderWidth: {
                    value:
                      (
                        parseFloat(buttonData?.borderWidth?.value!) /
                        parentRawFontSize
                      )
                        .toFixed(3)
                        .toString() || "",
                    unit: unitVal,
                  },
                });
              }
            }}
          />

          {/* Border Color */}
          <SatoriColorPicker
            label={"Border Color"}
            value={buttonData.borderColor || ""}
            onChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                borderColor: val,
              })
            }
          />

          {/* Border Radius */}
          <div className="flex items-center gap-2 my-2 w-full justify-between">
            <span className="text-base">Border Radius</span>
            <button
              onClick={() =>
                contentManager.setButtonConfig(blockId, {
                  borderRadius: {
                    ...buttonData?.borderRadius!,
                    linked: !buttonData?.borderRadius?.linked,
                  },
                })
              }
              className="ml-1 p-1"
              title={
                buttonData?.borderRadius?.linked
                  ? "Unlink sides"
                  : "Link all sides"
              }
            >
              {buttonData?.borderRadius?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {buttonData?.borderRadius?.linked ? (
            <div className="flex flex-col gap-2">
              <SatoriDimensionsBox
                value={buttonData?.borderRadius?.topLeft || ""}
                unit={buttonData?.borderRadius?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    borderRadius: {
                      ...buttonData?.borderRadius!,
                      topLeft: val,
                      topRight: val,
                      bottomRight: val,
                      bottomLeft: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedValue = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedValue,
                        topRight: convertedValue,
                        bottomRight: convertedValue,
                        bottomLeft: convertedValue,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedValue = (
                      parseFloat(buttonData?.borderRadius?.topLeft!) /
                      parentRawFontSize
                    )
                      .toFixed(3)
                      .toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedValue,
                        topRight: convertedValue,
                        bottomRight: convertedValue,
                        bottomLeft: convertedValue,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <SatoriDimensionsBox
                label={"Top Left"}
                value={
                  cleanDimenValues(buttonData?.borderRadius?.topLeft!) || ""
                }
                unit={buttonData?.borderRadius?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    borderRadius: {
                      ...buttonData?.borderRadius!,
                      topLeft: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Top Right"}
                value={
                  cleanDimenValues(buttonData?.borderRadius?.topRight!) || ""
                }
                unit={buttonData?.borderRadius?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    borderRadius: {
                      ...buttonData?.borderRadius!,
                      topRight: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom Right"}
                value={
                  cleanDimenValues(buttonData?.borderRadius?.bottomRight!) || ""
                }
                unit={buttonData?.borderRadius?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    borderRadius: {
                      ...buttonData?.borderRadius!,
                      bottomRight: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom Left"}
                value={
                  cleanDimenValues(buttonData?.borderRadius?.bottomLeft!) || ""
                }
                unit={buttonData?.borderRadius?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setButtonConfig(blockId, {
                    borderRadius: {
                      ...buttonData?.borderRadius!,
                      bottomLeft: val,
                    },
                  })
                }
                onUnitChange={(unitVal) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(buttonData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setButtonConfig(blockId, {
                      ...buttonData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: buttonData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          )}
        </div>
      </SatoriCollapsiblePanel>

      {/* 6. Layout */}
      <SatoriCollapsiblePanel
        icon={<RulerDimensionLine size={18} />}
        title={"Layout"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          <SatoriDropDownList
            layout={"horizontal"}
            isFontFamily={false}
            label={"Alignment"}
            options={ALIGN_OPTIONS}
            value={buttonData.alignment || "center"}
            onChange={val =>
              contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  alignment: val,
                })
            }
          />
          <SatoriDimensionsBox
            value={cleanDimenValues(buttonData.width?.value!) || "auto"}
            unit={buttonData.width?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setButtonConfig(blockId, {
                ...buttonData,
                width: { value: val, unit: buttonData?.width?.unit || "rem" },
              })
            }
            onUnitChange={(unitVal) => {
              if (unitVal === "px") {
                // We need to change from rem/em value to pixel value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  width: {
                    value:
                      Math.round(
                        buttonData.width?.unit === "%" 
                          ? 150
                          : parseFloat(buttonData?.width?.value!) * parentRawFontSize
                      ).toString() || "",
                    unit: unitVal,
                  },
                });
              } else if (unitVal === "rem" || unitVal === "em") {
                // We need to change from pixel value to rem/em value
                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  width: {
                    value:
                      (parseFloat(buttonData?.width?.value!) / parentRawFontSize)
                        .toFixed(3)
                        .toString() || "",
                    unit: unitVal,
                  },
                });
              } else {
                // If user switches to %, clamp value at 100
                let percentVal = parseFloat(buttonData?.width?.value!) || 0;
                if (percentVal > 100) percentVal = 100;

                contentManager.setButtonConfig(blockId, {
                  ...buttonData,
                  width: {
                    value: percentVal.toString(),
                    unit: unitVal,
                  },
                });
              }
            }}
          />
        </div>
        
      </SatoriCollapsiblePanel>
    </section>
  );
});
